import { FontWeight, SimpleMarkdown, Typography, TypographyVariant } from '@elearning-platform/ui';
import React, { useContext } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { PageDataContext } from '../../components/page';
import { CalculationType } from '../../enums';
import { BenefitCalculatorDescriptionParagraph, PageData } from '../../interfaces';
import { Calculation } from '../calculation/calculation.component';
import { CenteredContentSlideWrapper } from '../centeredContentSlideWrapper/centeredContentSlideWrapper.component';

import { Container, Disclaimer, Paragraphs, ParagraphTitle, ParagraphWrapper } from './benefitCalculator.styled';
import { BenefitCalculatorProps } from './benefitCalculator.types';

export const BenefitCalculator: React.FC<BenefitCalculatorProps> = (
  props: BenefitCalculatorProps
): JSX.Element => {
  const { callComplete, completed, centeredContentSlideWrapperData, baseUrl, data }: BenefitCalculatorProps = props;
  const dataCtx: Partial<PageData> | null = useContext(PageDataContext);
  const { slidesOverallCounter, currentSlideCounter, title }: Partial<PageData> = dataCtx ?? {};
  const { t }: TransProps<never> = useTranslation();
  const result: number = data.firstFieldValue - data.secondFieldValue;

  const handleSolvedStateChange: (solved: boolean) => void = (solved: boolean): void => {
    if (solved && !completed) {
      callComplete();
    }
  };

  return (
    <CenteredContentSlideWrapper
      currentSlideCounter={currentSlideCounter}
      leftImageSrc={baseUrl + centeredContentSlideWrapperData.leftImageSrc}
      rightImageSrc={baseUrl + centeredContentSlideWrapperData.rightImageSrc}
      slidesOverallCounter={slidesOverallCounter}
      title={title}
    >
      <Container>
        <Disclaimer>
          <Typography variant={TypographyVariant.TextMD}>
            <SimpleMarkdown text={data.disclaimer} />
          </Typography>
        </Disclaimer>
        <Paragraphs>
          {data.descriptionParagraphs.map((paragraph: BenefitCalculatorDescriptionParagraph): JSX.Element => (
            <ParagraphWrapper key={paragraph.title}>
              <Typography fontWeight={FontWeight.SemiBold} variant={TypographyVariant.TextLG}>
                <ParagraphTitle text={paragraph.title} />
              </Typography>
              <Typography variant={TypographyVariant.TextMD}>
                <SimpleMarkdown text={paragraph.text} />
              </Typography>
            </ParagraphWrapper>
          ))}
        </Paragraphs>
        <Calculation
          answer={result}
          calculationType={CalculationType.Substraction}
          correctAnswerText={t(
            'competence.app.scrollLesson.componentsV2.benefitCalculator.correctAnswerText',
          )}
          correctionText={t(
            'competence.app.scrollLesson.componentsV2.benefitCalculator.correctionText',
            { firstValue: data.firstFieldValue, secondValue: data.secondFieldValue, result }
          )}
          firstFieldData={{
            label: 'competence.app.scrollLesson.componentsV2.benefitCalculator.firstFieldLabel',
            unit: 'zł',
          }}
          onSolvedStateChange={handleSolvedStateChange}
          resultFieldData={{
            label: data.resultFieldLabel,
            unit: 'zł',
          }}
          secondFieldData={{
            label: 'competence.app.scrollLesson.componentsV2.benefitCalculator.secondFieldLabel',
            unit: 'zł',
          }}
        />
      </Container>
    </CenteredContentSlideWrapper>
  );
};
